import React, { useEffect, useRef, useState } from "react";
import ReactCrop, { Crop } from "react-image-crop";
import CancelButton from "./cancel-button";
import Button from "./button";
import { getFileStorageUrlSync } from "@/utils/storage";

interface CropImageModalProps {
  image: string;
  title: string;
  imageDimensions: { width: number; height: number };
  onClose: () => void;
  onApply: (file: File) => void;
}

function CropImageModal({
  image,
  title,
  imageDimensions,
  onClose,
  onApply,
}: CropImageModalProps) {
  const getInitialCropDimensions = () => {
    const maxWidth = Math.min(window.innerWidth, 430);
    const aspectRatio = imageDimensions.height / imageDimensions.width;
    const backgroundAspectRatio = 180 / 430;

    if (title === "BACKGROUND") {
      if (aspectRatio > backgroundAspectRatio) {
        return {
          width: maxWidth,
          height: maxWidth * backgroundAspectRatio,
        };
      } else {
        return {
          width: maxWidth * aspectRatio * (430 / 180),
          height: maxWidth * aspectRatio,
        };
      }
    } else {
      const zoomRatio =
        imageDimensions.width / Math.min(window.innerWidth, 430);
      // Calculate maximum crop size based on the smallest dimension of the displayed image
      const displayedWidth = imageDimensions.width / zoomRatio;
      const displayedHeight = imageDimensions.height / zoomRatio;
      const maxCropSize = Math.min(displayedWidth, displayedHeight);

      return {
        width: maxCropSize,
        height: maxCropSize,
      };
    }
  };

  const initialDimensions = getInitialCropDimensions();
  const [crop, setCrop] = useState<Crop>({
    unit: "px",
    height: initialDimensions.height,
    width: initialDimensions.width,
    x: 0,
    y: 0,
  });

  console.log(
    crop,
    "crop",
    Math.min(window.innerWidth, 430) *
      (imageDimensions.height / imageDimensions.width),
    "imageDimensions"
  );

  useEffect(() => {
    const img = new Image();
    img.src = image;
  }, [image, title]);

  const [completedCrop, setCompletedCrop] = useState<Crop>({
    unit: "px",
    height: initialDimensions.height,
    width: initialDimensions.width,
    x: 0,
    y: 0,
  });
  const imgRef = useRef<HTMLImageElement>(null);
  const getCroppedImg = (
    image: HTMLImageElement,
    crop: Crop
  ): Promise<File> => {
    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    // console.log(scaleX, "scaleX", scaleY, "scaleY");

    const cropWidth = (crop.width ?? 0) * scaleX;
    const cropHeight = (crop.height ?? 0) * scaleY;

    canvas.width = cropWidth;
    canvas.height = cropHeight;
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("No 2d context");
    }

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    ctx.drawImage(
      image,
      (crop.x ?? 0) * scaleX,
      (crop.y ?? 0) * scaleY,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight
    );

    return new Promise(resolve => {
      canvas.toBlob(
        blob => {
          if (blob) {
            resolve(
              new File([blob], "cropped-image.jpg", { type: "image/jpeg" })
            );
          }
        },
        "image/jpeg",
        1
      );
    });
  };

  const handleApply = async () => {
    if (completedCrop?.width && completedCrop?.height && imgRef.current) {
      try {
        const croppedImage = await getCroppedImg(imgRef.current, completedCrop);
        onApply(croppedImage);
      } catch (e) {
        console.error(e);
      }
    }
  };

  return (
    <div className="bg-black bg-opacity-50 flex items-center justify-center w-full max-w-[430px] mx-auto">
      <div className="bg-black w-full">
        <div className="flex justify-between items-center px-[20px] py-[10px] bg-[#0099FE] w-full relative">
          <span className="text-[26px]">CROP IMAGE</span>
          <CancelButton onClick={onClose} />
        </div>
        <div className="overflow-hidden">
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={c => setCompletedCrop(c)}
            aspect={title === "BACKGROUND" ? 430 / 180 : 1}
            locked={false}
            className="w-full h-full"
          >
            <img
              ref={imgRef}
              src={getFileStorageUrlSync(image)}
              alt="Crop me"
              className="w-full h-full object-contain"
            />
          </ReactCrop>
        </div>
        <div className="flex justify-center items-center px-[50px] pb-[30px]">
          <Button
            className="w-full custom-aspect-320-63 text-[24px] hover:cursor-pointer hover:scale-105 transition-transform duration-300 ease-in-out"
            image="/assets/button.svg"
            text="APPLY"
            onClick={handleApply}
          />
        </div>
      </div>
    </div>
  );
}

export default CropImageModal;
